import { __read } from "tslib";
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useQuery, useQueryCache, } from '@office-start/data-fetching';
import { useEdgeworthConfig } from '@office-start/edgeworth-config-service';
import { createGuid } from '@office-start/guid-generator';
import { useIdentity } from '@office-start/identity-service';
import { logGenericEvent, logRequestEvent, } from '@office-start/instrumentation-service';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { IDLE_VERYLOW_PRIORITY, scheduleWork, } from '@office-start/scheduler-service';
import { logError, logFeature, logRequest, } from '@office-start/telemetry-service';
import { getUserItem } from '@office-start/web-storage-utility';
import { useCallback, useMemo, useRef } from 'react';
import { deleteItemsFromCache } from '../operations/deleteItemsFromCache';
import { getCachedSyncTimestampStorageKey } from '../operations/EdgeworthDb';
import { fetchDeltaSyncPage, } from '../operations/fetchDeltaSyncPage';
import { getDeltaSyncRequestApiOptions } from '../utilities/RequestUtilities';
export function useEdgeworthDeltaSyncData() {
    var nextPageRef = useRef('');
    var pageNumberRef = useRef(1);
    var syncIdRef = useRef('');
    var lastSyncTimestampRef = useRef(undefined);
    var requestIdRef = useRef('');
    var isInitialStableRef = useRef(false);
    var startSyncMarkerRef = useRef(0);
    var hasFailuresRef = useRef(false);
    var isErrorRef = useRef(false);
    var queryCache = useQueryCache();
    var context = useOfficeComponentContext().data;
    var edgeworthConfig = useEdgeworthConfig().data;
    var identity = useIdentity().data;
    var syncThrottleWindowInMillisecond = useMemo(function () {
        return edgeworthConfig
            ? edgeworthConfig.tabFocusSyncThrottleWindowInSeconds * 1000
            : 0;
    }, [edgeworthConfig]);
    if (!syncIdRef.current) {
        syncIdRef.current = createGuid();
    }
    if (!requestIdRef.current) {
        requestIdRef.current = createGuid();
    }
    var syncStorageKeyRef = useRef('');
    if (!syncStorageKeyRef.current) {
        var storageKey = getCachedSyncTimestampStorageKey();
        if (storageKey) {
            syncStorageKeyRef.current = storageKey;
        }
    }
    if (lastSyncTimestampRef.current === undefined &&
        identity &&
        syncStorageKeyRef.current) {
        var rawPreviousTimestamp = getUserItem(identity.puid, syncStorageKeyRef.current);
        lastSyncTimestampRef.current = rawPreviousTimestamp || null;
    }
    var queryKey = useMemo(function () { return ['EdgeworthData', "DeltaSync" /* QuerySubtype.DeltaSync */]; }, []);
    var resetStates = useCallback(function () {
        syncIdRef.current = createGuid();
        requestIdRef.current = createGuid();
        pageNumberRef.current = 1;
        nextPageRef.current = '';
    }, []);
    var query = useQuery(queryKey, function () {
        var _a;
        var currentPage = ((_a = context === null || context === void 0 ? void 0 : context.router) === null || _a === void 0 ? void 0 : _a.getCurrentPageForLogging())
            ? context.router.getCurrentPageForLogging()
            : '';
        // Check below is to ensure we set the correct states for a new sync cycle
        if (!isInitialStableRef.current && nextPageRef.current === '') {
            // Mark when we start initial sync
            startSyncMarkerRef.current = performance.now();
            // Set hasFailures to false
            hasFailuresRef.current = false;
        }
        var apiOptions = getDeltaSyncRequestApiOptions(
        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        edgeworthConfig, context, identity, 
        /* eslint-enable @typescript-eslint/no-non-null-assertion */
        pageNumberRef.current, requestIdRef.current, syncIdRef.current, currentPage, lastSyncTimestampRef.current || '', nextPageRef.current);
        if (edgeworthConfig === null || edgeworthConfig === void 0 ? void 0 : edgeworthConfig.backgroundSyncEnabled) {
            apiOptions.enableBackgroundSync = true;
        }
        return fetchDeltaSyncPage(apiOptions).catch(function (err) {
            hasFailuresRef.current = true;
            throw err;
        });
    }, {
        enabled: !!(edgeworthConfig && context && identity),
        staleTime: syncThrottleWindowInMillisecond,
        onSettled: function (data) {
            // Manage looping using onSettled because useQuery returns undefined query.data when query is executing
            // Which means if we are using useEffect hook, we will not be able to rerender by setting a state when query finally returns data
            if (data) {
                var items_1 = data.items, nextPageLink = data.nextPageLink, version = data.version, hasFailures = data.hasFailures;
                // set sync timestamp for new sync
                if (items_1 === null || items_1 === void 0 ? void 0 : items_1.length) {
                    var _a = __read(items_1, 1), latestItem = _a[0];
                    var latestTimestamp = version === 2
                        ? latestItem.last_store_modified_datetime
                        : latestItem.last_recommended_modified_datetime;
                    hasFailuresRef.current =
                        hasFailuresRef.current || hasFailures;
                    // Reset isError
                    isErrorRef.current = false;
                    // Only update last sync timestamp if there had been no failures
                    if (!hasFailuresRef.current &&
                        (!lastSyncTimestampRef.current ||
                            latestTimestamp > lastSyncTimestampRef.current)) {
                        lastSyncTimestampRef.current = latestTimestamp;
                    }
                    // Handle file deletions in IDB
                    scheduleWork(function () {
                        deleteItemsFromCache(items_1.reduce(function (acc, item) {
                            if (item.is_deleted) {
                                acc.push(item.id);
                            }
                            return acc;
                        }, [])).catch(function (err) {
                            var _a;
                            var _b;
                            var error = (_b = err === null || err === void 0 ? void 0 : err.toString) === null || _b === void 0 ? void 0 : _b.call(err);
                            logGenericEvent(KnownInstrumentationEvents.EdgeworthCacheDeleteError, (_a = {},
                                _a["Error" /* InstrumentationParam.Error */] = error || '',
                                _a));
                            // TODO: Remove above Aria logging after fully migrating to OTEL
                            logError({
                                eventName: 'Edgeworth_Cache_Delete_Error',
                                errorType: 'Edgeworth_Cache_Delete_Error',
                                message: error || '',
                            });
                        });
                    }, IDLE_VERYLOW_PRIORITY);
                }
                // There are a few conditions which we want to do a hard stop from fetching:
                // - nextPageLink is falsy, so there's no subsequent page
                // - server returns the same nextPageLink, so we are calling the exact same request again
                // - pageNumber is over 40 (backend has a hard limit of ~4000 documents)
                if (!nextPageLink ||
                    nextPageRef.current === nextPageLink ||
                    pageNumberRef.current > 40) {
                    if (startSyncMarkerRef.current > 0) {
                        var latency_1 = performance.now() - startSyncMarkerRef.current;
                        scheduleWork(function () {
                            var _a;
                            var params = (_a = {},
                                _a["ActualSize" /* InstrumentationParam.ActualSize */] = items_1.length.toString(),
                                _a["ItemId" /* InstrumentationParam.ItemId */] = syncIdRef.current,
                                _a["ItemCount" /* InstrumentationParam.ItemCount */] = pageNumberRef.current.toFixed(),
                                _a["EventType" /* InstrumentationParam.EventType */] = 'Request',
                                _a);
                            logRequestEvent(KnownInstrumentationEvents.EdgeworthFullSyncRequest, 200 /* HttpStatusCode.OK */, latency_1, undefined, false, params);
                            // TODO: Remove above Aria logging after fully migrating to OTEL
                            logRequest({
                                eventName: "EdgeworthFullSync" /* KnownRequestInstrumentationEvents.EdgeworthFullSyncRequest */,
                                httpCode: 200 /* HttpStatusCode.OK */,
                                error: undefined,
                                latency: latency_1,
                                fromCache: false,
                            });
                            logFeature({
                                featureName: "Edgeworth" /* KnownFeatureNames.Edgeworth */,
                                eventName: "EdgeworthFullSync" /* KnownRequestInstrumentationEvents.EdgeworthFullSyncRequest */,
                                params: params,
                                area: 'Edgeworth',
                            });
                        }, IDLE_VERYLOW_PRIORITY);
                        startSyncMarkerRef.current = 0;
                    }
                    isInitialStableRef.current = true;
                }
                else if (nextPageLink) {
                    nextPageRef.current = nextPageLink;
                    requestIdRef.current = createGuid();
                    pageNumberRef.current = pageNumberRef.current + 1;
                    queryCache.invalidateQueries(queryKey);
                    // Return early to avoid resetting all states
                    return;
                }
            }
            // Reset all states so when next refetch happen, we start a fresh sync.
            resetStates();
            isInitialStableRef.current = true;
        },
        onError: function () {
            isInitialStableRef.current = true;
            hasFailuresRef.current = true;
            isErrorRef.current = true;
        },
    });
    var resyncCallback = useCallback(function () {
        resetStates();
        queryCache.invalidateQueries(queryKey);
    }, [queryCache, queryKey, resetStates]);
    return useMemo(function () { return ({
        data: query.data,
        // only return timestamp when not syncing and there are no full/partial errors during sync
        lastSyncTimestamp: !query.isFetching && !hasFailuresRef.current
            ? lastSyncTimestampRef.current
            : undefined,
        resync: resyncCallback,
        // We can't use query.isError as RQ always tries to refetch on window focus, so
        // if failures persist, we'd flash between blank and error state
        isError: isErrorRef.current,
        isInitialStable: isInitialStableRef.current,
    }); }, [query.data, query.isFetching, resyncCallback]);
}
//# sourceMappingURL=useEdgeworthDeltaSyncData.js.map